import { createApiHooks, useGenericGet } from '../../../../../hooks/use-api.hook';
import { apiClient } from '../../../../../services/api-client.service';
import type {
  Workflow,
  WorkflowCreate,
  WorkflowGetFiltersResponse,
  WorkflowGetResponse,
  WorkflowUpdate,
} from '../types/workflow.types';
import type { UseListParams } from '../../../../../types/pagination.types';
import { useMemo } from 'react';
import { useActiveTenant } from '@states/active-tenant.atom';

const useWorkflowBasePath = () => {
  const { activeTenant } = useActiveTenant();
  return useMemo(() => {
    return `/v1/admin/tenants/${activeTenant?.id}/workflows`;
  }, [activeTenant]);
};

export const useWorkflowHooks = () => {
  const workflowBasePath = useWorkflowBasePath();
  const { activeTenant } = useActiveTenant();
  return createApiHooks<Workflow, WorkflowGetResponse, WorkflowCreate, WorkflowUpdate>({
    basePath: workflowBasePath,
    queryKey: ['Workflows', 'Tenant', activeTenant!.id],
    apiClient,
  });
};

export const useWorkflowGetFilters = (queryOptions?: any) => {
  const workflowBasePath = useWorkflowBasePath();
  return useGenericGet<WorkflowGetFiltersResponse>(
    `${workflowBasePath}/filters`,
    ['Workflows', 'filters'],
    apiClient,
    queryOptions
  );
};

// Create a custom hook that returns all the workflow hooks
export const useWorkflowApi = () => {
  const workflowHooks = useWorkflowHooks();

  return {
    useGet: workflowHooks.useGet,
    useCreate: workflowHooks.useCreate,
    useUpdate: workflowHooks.useUpdate,
    useDelete: workflowHooks.useDelete,
    useList: (params: UseListParams) => workflowHooks.useList(params),
  };
};

// Export individual hooks for backward compatibility
export const useWorkflowGet = (id: string | number, queryOptions?: any) => {
  const { useGet } = useWorkflowApi();
  return useGet(id, queryOptions);
};

export const useWorkflowCreate = (mutationOptions?: any) => {
  const { useCreate } = useWorkflowApi();
  return useCreate(mutationOptions);
};

export const useWorkflowUpdate = (mutationOptions?: any) => {
  const { useUpdate } = useWorkflowApi();
  return useUpdate(mutationOptions);
};

export const useWorkflowDelete = (mutationOptions?: any) => {
  const { useDelete } = useWorkflowApi();
  return useDelete(mutationOptions);
};

export const useWorkflowList = (params: UseListParams) => {
  const { useList } = useWorkflowApi();
  return useList(params);
};
